import { Link, useRouterState, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";

interface NavLinkProps {
  to: string;
  label: string;
  count?: string;
  activeKey?: string;
}

function NavLink({ to, label, count, activeKey }: NavLinkProps) {
  const state = useRouterState();
  const pathname = state.location.pathname;
  const isActive = activeKey
    ? pathname === activeKey
    : pathname === to || pathname.startsWith(to + "/");

  return (
    <Link to={to} className={isActive ? "is-active" : ""}>
      <span>{label}</span>
      {count !== undefined && (
        <span
          style={{ marginLeft: "auto", color: "var(--ink-mute)", fontSize: 10 }}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

function initials(email: string) {
  const parts = email.split("@")[0].split(/[._-]/);
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function AdminSidebar() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate({ to: "/login" });
  }

  return (
    <aside className="adm__side">
      <div className="adm__brand">
        <span className="brand__mark" aria-hidden="true" />
        <div style={{ lineHeight: 1.1 }}>
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              letterSpacing: "0.24em",
              color: "var(--ink)",
              fontWeight: 600,
            }}
          >
            PROVENANCE
          </div>
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 9,
              letterSpacing: "0.22em",
              color: "var(--accent)",
              marginTop: 2,
            }}
          >
            ADMIN · OPS
          </div>
        </div>
      </div>

      <nav className="adm__nav">
        <NavLink to="/dpps" label="Passports" />
        <NavLink to="/submissions" label="Form submissions" />
        {can("dpp:transfer") && (
          <NavLink to="/transfers" label="Transfers" />
        )}
        {can("issue:view") && <NavLink to="/issues" label="Issues" />}
        <NavLink to="/analytics" label="Analytics" />
        {can("user:view") && <NavLink to="/customers" label="Customers" />}
        {can("admin:view") && <NavLink to="/users" label="Users" />}
      </nav>

      <div
        style={{
          marginTop: "auto",
          borderTop: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            padding: "16px 22px",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              background: "var(--surface-3)",
              display: "grid",
              placeItems: "center",
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              color: "var(--ink)",
              flexShrink: 0,
            }}
          >
            {user ? initials(user.email) : "—"}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.3, minWidth: 0 }}>
            <div
              style={{
                color: "var(--ink)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user?.email ?? "—"}
            </div>
            <div
              style={{
                fontFamily: "var(--f-mono)",
                fontSize: 9,
                letterSpacing: "0.14em",
                color: "var(--ink-mute)",
                textTransform: "uppercase",
                marginTop: 2,
              }}
            >
              {user?.role ?? "—"}
            </div>
          </div>
        </div>

        <button
          onClick={handleLogout}
          style={{
            width: "100%",
            padding: "12px 22px",
            background: "transparent",
            border: 0,
            borderTop: "1px solid var(--line)",
            color: "var(--ink-mute)",
            fontFamily: "var(--f-mono)",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            textAlign: "left",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--danger)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--ink-mute)";
          }}
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.2"
          >
            <path d="M8 6H2M5 3l-3 3 3 3M8 2h2a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H8" />
          </svg>
          Sign out
        </button>
      </div>
    </aside>
  );
}
